/**
 * The two product shots: a phone screen and a desktop panel.
 *
 * Each carries a slot (`data-live`) that live.js fills with the tracker itself:
 * its preview document, the real views on a sample ledger with no account and
 * no requests -- which answers both reasons this used to be a drawing (no guest
 * account per visitor, and only that one document may be framed, only here).
 *
 * The drawing below stays as what is shown until the app is ready, and as all
 * that is shown if it never is: no script, another origin, a failed load.
 *
 * What keeps this honest: the numbers come from `/api/landing/stats`, the same
 * source as the ticker. It is a drawing of the app, not a claim about anybody's
 * balance.
 */

import { h } from './dom.js';

/** Deterministic pseudo-random heights, so the bars look organic but never jump. */
function bars(count, seed) {
  const out = [];
  let x = seed;
  for (let i = 0; i < count; i++) {
    x = (x * 1103515245 + 12345) % 2147483648;
    const ratio = 0.34 + (x / 2147483648) * 0.46 + (i / count) * 0.2;
    out.push(Math.min(1, ratio));
  }
  return out;
}

/**
 * Pulls a labelled cell out of the ticker row by position.
 *
 * By index rather than by label, because the labels are translated and the row's
 * order is the contract — see `CURATED_TICKER` in shared/landing-content.js.
 */
const cell = (ticker, index, fallback) => ticker?.[index]?.value || fallback;

export function phoneMock({ content, ticker }) {
  const t = content;
  const rows = t.panels[0].tags;

  return h('div', { class: 'mock', 'aria-hidden': 'true' },
    h('div', { class: 'mock__top' },
      h('img', { src: '/logo-mark.png', alt: '', width: 20, height: 20, style: 'width:20px;height:20px;object-fit:contain' }),
      h('div', {},
        h('div', { class: 'mock__title' }, 'Portfolio Tracker'),
        h('div', { class: 'mock__sub' }, t.productKicker)
      )
    ),

    h('div', { class: 'mock__hero' },
      h('div', { class: 'mock__label' }, ticker?.[0]?.label?.toUpperCase() || 'NET WORTH'),
      h('div', { class: 'mock__big' }, t.mock.netWorth),
      h('div', { class: 'mock__delta' }, cell(ticker, 0, '+4,7%')),
      h('div', { class: 'mock__bars' },
        bars(12, 7).map(v => h('div', { class: 'mock__bar', style: `height:${Math.round(v * 100)}%` }))
      )
    ),

    h('div', { class: 'mock__pair' },
      h('div', { class: 'mock__tile' },
        h('div', { class: 'mock__label' }, rows[2]?.toUpperCase() || 'SAVINGS'),
        h('div', { class: 'mock__tileVal' }, cell(ticker, 1, '31%'))
      ),
      h('div', { class: 'mock__tile' },
        h('div', { class: 'mock__label' }, t.panels[2].tags[1]?.toUpperCase() || 'P/L'),
        h('div', { class: 'mock__tileVal' }, cell(ticker, 3, '+18,2%'))
      )
    ),

    h('div', { class: 'mock__rows' },
      [
        [t.panels[1].title.split(' ')[0], cell(ticker, 2, '−12 340'), '#EF4E43'],
        [t.panels[2].tags[0], cell(ticker, 3, '+18,2%'), '#3DD9A0'],
        [t.panels[2].tags[2], cell(ticker, 4, '0,19'), '#6FE8BC']
      ].map(([label, value, colour]) =>
        h('div', { class: 'mock__row' },
          h('span', { class: 'mock__chip', style: `background:${colour}` }),
          h('span', { class: 'mock__rowLabel' }, label),
          h('span', { class: 'mock__rowVal' }, value)
        )
      )
    ),

    h('div', { class: 'mock__tabs' },
      t.mock.tabs.map((label, i) =>
        h('div', { class: 'mock__tab' + (i === 0 ? ' mock__tab--on' : '') }, label)
      )
    )
  );
}

export function phone({ content, ticker }) {
  return h('div', { class: 'phone', 'data-phone': '1', 'data-live-host': '1' },
    h('div', { class: 'phone__key phone__key--l1' }),
    h('div', { class: 'phone__key phone__key--l2' }),
    h('div', { class: 'phone__key phone__key--l3' }),
    h('div', { class: 'phone__key phone__key--r' }),
    h('div', { class: 'phone__bezel' },
      h('div', { class: 'phone__screen' },
        h('div', { class: 'phone__notch' }, h('div', { class: 'phone__lens' })),
        h('div', { class: 'phone__gloss' }),
        phoneMock({ content, ticker }),
        h('div', { class: 'live live--phone', 'data-live': 'mobile' })
      )
    )
  );
}

export function desktopMock({ content, ticker, appHost }) {
  const t = content;

  return h('div', { class: 'browser', 'data-shot': '1' },
    h('div', { class: 'browser__bar' },
      h('div', { class: 'browser__lights' },
        h('div', { class: 'browser__light' }),
        h('div', { class: 'browser__light' }),
        h('div', { class: 'browser__light' })
      ),
      h('div', { class: 'browser__urlWrap' }, h('div', { class: 'browser__url' }, appHost))
    ),

    h('div', { class: 'browser__stage' },
      h('div', { class: 'live live--desk', 'data-live': 'desktop' }),
    h('div', { class: 'browser__body', 'aria-hidden': 'true' },
      h('div', { class: 'desk__top' },
        h('div', { class: 'desk__panel' },
          h('div', { class: 'mock__label' }, ticker?.[0]?.label?.toUpperCase() || 'NET WORTH'),
          h('div', { class: 'desk__val' }, t.mock.netWorth),
          h('div', { class: 'mock__delta' }, cell(ticker, 0, '+4,7%')),
          h('div', { class: 'desk__bars' },
            bars(18, 21).map(v => h('div', { class: 'desk__bar', style: `height:${Math.round(v * 100)}%` }))
          )
        ),
        h('div', { class: 'desk__panel' },
          h('div', { class: 'mock__label' }, (ticker?.[1]?.label || 'Savings rate').toUpperCase()),
          h('div', { class: 'desk__val' }, cell(ticker, 1, '31%')),
          h('div', { class: 'card__meter', style: 'margin-top:auto' },
            h('div', { class: 'card__fill', style: 'width:31%;background:#3DD9A0' })
          )
        ),
        h('div', { class: 'desk__panel' },
          h('div', { class: 'mock__label' }, (ticker?.[3]?.label || 'Portfolio').toUpperCase()),
          h('div', { class: 'desk__val' }, cell(ticker, 3, '+18,2%')),
          h('div', { class: 'card__meter', style: 'margin-top:auto' },
            h('div', { class: 'card__fill', style: 'width:68%;background:#6FE8BC' })
          )
        )
      ),

      h('div', { class: 'desk__grid' },
        h('div', { class: 'desk__panel' },
          h('div', { class: 'mock__label' }, t.features[1].title.toUpperCase()),
          h('div', { class: 'desk__rows' },
            t.mock.spend.map(([label, pct]) =>
                h('div', { class: 'desk__row' },
                  h('span', { style: 'min-width:78px' }, label),
                  h('span', { class: 'desk__rowBar' },
                    h('span', { class: 'desk__rowFill', style: `width:${pct}%` })
                  ),
                  h('span', { class: 'desk__rowVal' }, pct + '%')
                )
              )
          )
        ),
        h('div', { class: 'desk__panel' },
          h('div', { class: 'mock__label' }, t.panels[1].tags[1].toUpperCase()),
          h('div', { class: 'desk__rows' },
            t.mock.debts.map(([label, rate, pct]) =>
              h('div', { class: 'desk__row' },
                h('span', { style: 'min-width:120px' }, label),
                h('span', { class: 'desk__rowBar' },
                  h('span', { class: 'desk__rowFill', style: `width:${pct}%;background:#EF4E43` })
                ),
                h('span', { class: 'desk__rowVal' }, rate)
              )
            )
          )
        )
      )
    )
    )
  );
}
